import React, { useState, useEffect, useMemo, useCallback } from 'react';
import './TradeEntryExitLogTable.css';
import { buildScopedApiUrl, ensureArray, getApiHeaders } from "./config";
import { useWorkspace } from "./workspace/WorkspaceContext";
import UtcIstClock from "./UtcIstClock";
import TradingViewChartIcon from "./TradingViewChartIcon";

const DEFAULT_PAGE_SIZE = 10;

export default function TradeEntryExitLogTable({ onClose }) {
  const { workspace } = useWorkspace();
  const DEFAULT_CUTOFF_DAYS = 3;
  const WEEK_CUTOFF_DAYS = 7;
  const MONTH_CUTOFF_DAYS = 30;
  const [logs, setLogs] = useState([]);
  const [filters, setFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [symbolOptions, setSymbolOptions] = useState([]);
  const [selectedSymbol, setSelectedSymbol] = useState('ALL');
  const [selectedTimeFrame, setSelectedTimeFrame] = useState('ALL');
  const [cutoffDays, setCutoffDays] = useState(DEFAULT_CUTOFF_DAYS);
  const timeFrameOptions = ['5', '15', '30', '60', '240', '480', '720', '1440'];
  const [selectedCandleCount, setSelectedCandleCount] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const candleCountOptions = ['5', '10', '20', '30', '50', '100'];

  const flatLogs = useMemo(
    () => ensureArray(logs).map((log) => {
      const row = { ...log.id, ...log };
      if (String(row.entryExitType).toUpperCase() === 'EXIT' && row.holdSide) {
        row.holdSide = `close_${String(row.holdSide).replace(/^(open_|close_)/i, '')}`;
      }
      return row;
    }),
    [logs]
  );

  const actionOptions = useMemo(() => {
    const set = new Set();
    flatLogs.forEach((log) => log.action && set.add(log.action));
    return Array.from(set);
  }, [flatLogs]);

  const candleCloseTimeOptions = useMemo(() => {
    const set = new Set();
    flatLogs.forEach((log) => log.calculatedCandleCloseTime && set.add(log.calculatedCandleCloseTime));
    return Array.from(set).sort();
  }, [flatLogs]);

  const headers = {
    symbol: 'Symbol',
    chart: 'Chart',
    unrealizedPL: 'Unrealized P/L',
    holdSide: 'Hold Side',
    entryExitType: 'Entry/Exit',
    trackerType: 'Tracker Type',
    calculatedCandleCloseTime: 'Candle Close Time',
    margin: 'Margin',
    averageOpenPrice: 'Avg Open Price',
    timeFrame: 'Time Frame',
    marketPrice: 'Market Price',
    alertDirection: 'Alert Direction',
    achievedProfits: 'Achieved Profits',
    total: 'Total',
    locked: 'Locked',
    available: 'Available',
    leverage: 'Leverage',
    orderId: 'Order ID',
    createdDate: 'Created Date',
    sgTimestamp: 'SG Timestamp',
    action: 'Action'
  };

  const columns = Object.keys(headers);

  useEffect(() => {
    if (selectedTimeFrame === 'ALL') {
      setSelectedCandleCount('');
    }
  }, [selectedTimeFrame]);

  useEffect(() => {
    fetch(buildScopedApiUrl('/api/bot/ui/get/getRecentActiveAlertSymbols', workspace), {
      headers: getApiHeaders()
    })
      .then((res) => res.json())
      .then((data) => {
        const safeSymbols = ensureArray(data);
        if (!Array.isArray(data)) {
          console.warn("Unexpected trade entry/exit symbol payload shape", workspace, data);
        }
        setSymbolOptions(['ALL', ...Array.from(new Set(safeSymbols))]);
      })
      .catch(console.error);
  }, [workspace]);

  const fetchLogsBySelection = useCallback((cutoffDaysOverride) => {
    let url = '';
    const effectiveCutoffDays = cutoffDaysOverride ?? cutoffDays;

    if (selectedSymbol === 'ALL' && selectedTimeFrame !== 'ALL' && selectedCandleCount !== '') {
      url = buildScopedApiUrl('/api/bot/ui/get/getTradeEntryExitLogByTimeFrameAndCandleCloseTimes', workspace, {
        timeFrame: selectedTimeFrame,
        noOfLatestCloseTimes: selectedCandleCount,
      });
    } else if (selectedSymbol === 'ALL' && selectedTimeFrame === 'ALL') {
      url = buildScopedApiUrl('/api/bot/ui/get/getAllTradeEntryExitLogByCutOff', workspace, {
        hours: effectiveCutoffDays * 24,
      });
    } else if (selectedTimeFrame === 'ALL') {
      url = buildScopedApiUrl('/api/bot/ui/get/getAllTradeEntryExitLogBySymbol', workspace, {
        symbol: selectedSymbol,
      });
    } else if (selectedSymbol === 'ALL') {
      url = buildScopedApiUrl('/api/bot/ui/get/getAllTradeEntryExitLogByTimeFrame', workspace, {
        timeFrame: selectedTimeFrame,
      });
    } else {
      url = buildScopedApiUrl('/api/bot/ui/get/getAllTradeEntryExitLogBySymbolAndTimeFrame', workspace, {
        symbol: selectedSymbol,
        timeFrame: selectedTimeFrame,
      });
    }

    fetch(url, {
      headers: getApiHeaders()
    })
      .then((res) => res.json())
      .then((data) => {
        const safeLogs = ensureArray(data);
        if (!Array.isArray(data)) {
          console.warn("Unexpected response format for logs:", data);
        }
        setLogs(safeLogs);
        setPage(1);
      })
      .catch(console.error);
  }, [selectedSymbol, selectedTimeFrame, selectedCandleCount, cutoffDays, workspace]);

  useEffect(() => {
    fetchLogsBySelection();
  }, [fetchLogsBySelection]);

  const handleFilterChange = (key, value) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleSort = (key) => {
    setPage(1);
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const filteredLogs = flatLogs.filter((log) =>
    columns.every((key) => {
      const filterVal = filters[key];
      const fieldVal = log[key];

      if (!filterVal) return true;

      if (key === 'unrealizedPL' || key === 'timeFrame' || key === 'averageOpenPrice') {
        const numVal = parseFloat(fieldVal);
        const match = filterVal.match(/^([<>]=?|=)?\s*(-?\d+(\.\d+)?)$/);
        if (match) {
          const op = match[1] || '=';
          const filterNum = parseFloat(match[2]);
          if (Number.isNaN(numVal)) return false;

          switch (op) {
            case '>': return numVal > filterNum;
            case '>=': return numVal >= filterNum;
            case '<': return numVal < filterNum;
            case '<=': return numVal <= filterNum;
            case '=':
            default:
              return numVal === filterNum;
          }
        }

        return fieldVal?.toString().includes(filterVal);
      }

      return fieldVal?.toString().toLowerCase().includes(filterVal.toLowerCase());
    })
  );

  const sortedLogs = [...filteredLogs].sort((a, b) => {
    const { key, direction } = sortConfig;
    if (!key) return 0;

    const numericKeys = ['unrealizedPL', 'averageOpenPrice', 'timeFrame', 'margin', 'leverage', 'total', 'marketPrice'];

    let aVal = a[key];
    let bVal = b[key];

    if (numericKeys.includes(key)) {
      aVal = parseFloat(aVal);
      bVal = parseFloat(bVal);
      if (Number.isNaN(aVal)) return 1;
      if (Number.isNaN(bVal)) return -1;
    }

    if (aVal > bVal) return direction === 'asc' ? 1 : -1;
    if (aVal < bVal) return direction === 'asc' ? -1 : 1;
    return 0;
  });

  const totalPages = pageSize === 'all'
    ? 1
    : Math.max(1, Math.ceil(sortedLogs.length / pageSize));

  useEffect(() => {
    setPage((current) => Math.min(current, totalPages));
  }, [totalPages]);

  const pagedLogs = pageSize === 'all'
    ? sortedLogs
    : sortedLogs.slice((page - 1) * pageSize, page * pageSize);

  const firstVisibleRecord = sortedLogs.length === 0 || pageSize === 'all'
    ? (sortedLogs.length === 0 ? 0 : 1)
    : ((page - 1) * pageSize) + 1;
  const lastVisibleRecord = pageSize === 'all'
    ? sortedLogs.length
    : Math.min(page * pageSize, sortedLogs.length);

  const formatDate = (ts) => (ts ? new Date(ts).toLocaleString() : '-');

  return (
    <div className="trade-log-table-wrapper">
      <div className="trade-log-title-row">
        <span aria-hidden="true" />
        <h4>Trade Entry/Exit Logs</h4>
        <div className="trade-log-header-actions">
          <UtcIstClock compact />
          {onClose && <button type="button" className="panel-close-btn" onClick={onClose} aria-label="Close Trade Entry/Exit Logs" title="Close Trade Entry/Exit Logs" />}
        </div>
      </div>
      <p style={{ marginTop: 0, color: '#666' }}>
        Workspace: <strong>{workspace.exchange}</strong> / <strong>{workspace.marketType}</strong>
      </p>

      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <label>
          Symbol:&nbsp;
          <select value={selectedSymbol} onChange={(e) => setSelectedSymbol(e.target.value)}>
            {symbolOptions.map((sym) => (
              <option key={sym} value={sym}>{sym}</option>
            ))}
          </select>
        </label>

        <label>
          Time Frame:&nbsp;
          <select value={selectedTimeFrame} onChange={(e) => setSelectedTimeFrame(e.target.value)}>
            {['ALL', ...timeFrameOptions].map((tf) => (
              <option key={tf} value={tf}>{tf}</option>
            ))}
          </select>
        </label>

        <label>
          Recent Candle Count:&nbsp;
          <input
            type="text"
            list="tradeEntryExitCandleCountSuggestions"
            value={selectedCandleCount}
            onChange={(e) => setSelectedCandleCount(e.target.value)}
            placeholder="e.g. 10"
            disabled={selectedTimeFrame === 'ALL'}
          />
          <datalist id="tradeEntryExitCandleCountSuggestions">
            {candleCountOptions.map((opt) => (
              <option key={opt} value={opt} />
            ))}
          </datalist>
        </label>

        <button
          onClick={() => {
            setCutoffDays(DEFAULT_CUTOFF_DAYS);
            fetchLogsBySelection(DEFAULT_CUTOFF_DAYS);
          }}
        >
          Fetch Logs
        </button>
        <button
          onClick={() => {
            setCutoffDays(WEEK_CUTOFF_DAYS);
            fetchLogsBySelection(WEEK_CUTOFF_DAYS);
          }}
        >
          Fetch 7 Days
        </button>
        <button
          onClick={() => {
            setCutoffDays(MONTH_CUTOFF_DAYS);
            fetchLogsBySelection(MONTH_CUTOFF_DAYS);
          }}
        >
          Fetch 30 Days
        </button>
        <button className="trade-log-clear-filters-btn" onClick={() => {
          setFilters({});
          setPage(1);
        }}>
          <span className="trade-log-clear-filters-btn__icon" aria-hidden="true" />
          <span>Clear Filters</span>
        </button>
      </div>

      <div className="trade-log-table-scroll">
        <table className="trade-log-table">
        <thead>
          <tr>
            <th>S.No</th>
            {columns.map((col) => (
              <th key={col} onClick={col === 'chart' ? undefined : () => handleSort(col)}>
                {headers[col]} {sortConfig.key === col ? (sortConfig.direction === 'asc' ? '▲' : '▼') : ''}
              </th>
            ))}
          </tr>
          <tr>
            <th />
            {columns.map((col) => {
              if (col === 'chart') return <th key={col} />;
              const val = filters[col] || '';
              if (col === 'symbol') {
                return (
                  <th key={col}>
                    <select value={val} onChange={(e) => handleFilterChange(col, e.target.value)}>
                      <option value="">All</option>
                      {symbolOptions
                        .filter((sym) => sym !== 'ALL')
                        .map((sym) => <option key={sym} value={sym}>{sym}</option>)}
                    </select>
                  </th>
                );
              }

              if (col === 'entryExitType') {
                return (
                  <th key={col}>
                    <select value={val} onChange={(e) => handleFilterChange(col, e.target.value)}>
                      <option value="">All</option>
                      <option value="ENTRY">ENTRY</option>
                      <option value="EXIT">EXIT</option>
                    </select>
                  </th>
                );
              }

              if (col === 'action') {
                return (
                  <th key={col}>
                    <select value={val} onChange={(e) => handleFilterChange(col, e.target.value)}>
                      <option value="">All</option>
                      {actionOptions.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </th>
                );
              }

              if (col === 'timeFrame') {
                return (
                  <th key={col}>
                    <select value={val} onChange={(e) => handleFilterChange(col, e.target.value)}>
                      <option value="">All</option>
                      {timeFrameOptions.map((tf) => (
                        <option key={tf} value={tf}>{tf}</option>
                      ))}
                    </select>
                  </th>
                );
              }

              if (col === 'calculatedCandleCloseTime') {
                return (
                  <th key={col}>
                    <select value={val} onChange={(e) => handleFilterChange(col, e.target.value)}>
                      <option value="">All</option>
                      {candleCloseTimeOptions.map((time) => (
                        <option key={time} value={time}>{time}</option>
                      ))}
                    </select>
                  </th>
                );
              }

              return (
                <th key={col}>
                  <input
                    type="text"
                    placeholder={`Filter ${headers[col]}`}
                    value={val}
                    onChange={(e) => handleFilterChange(col, e.target.value)}
                  />
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {pagedLogs.map((log, idx) => (
            <tr key={`${log.symbol}|${log.calculatedCandleCloseTime}|${idx}`}>
              <td>{firstVisibleRecord + idx}</td>
              {columns.map((col) => {
                if (col === 'chart') {
                  return (
                    <td key={col}>
                      <TradingViewChartIcon
                        symbol={log.symbol}
                        timeFrame={log.timeFrame}
                        exchange={log.exchange || workspace.exchange}
                        marketType={log.marketType || workspace.marketType}
                      />
                    </td>
                  );
                }
                const value = log[col];
                const isUnrealizedPL = col === 'unrealizedPL';
                const isExitType = col === 'entryExitType';
                const numericVal = parseFloat(value);
                const cellStyle = isUnrealizedPL
                  ? numericVal > 0
                    ? { backgroundColor: '#d1fae5' }
                    : numericVal < 0
                      ? { backgroundColor: '#fee2e2' }
                      : {}
                  : isExitType && value === 'EXIT'
                    ? { color: '#dc2626', fontWeight: 600 }
                    : {};

                return (
                  <td key={col} style={cellStyle}>
                    {col.toLowerCase().includes('date') || col.includes('Timestamp')
                      ? formatDate(value)
                      : value || '-'}
                  </td>
                );
              })}
            </tr>
          ))}
          {pagedLogs.length === 0 && (
            <tr>
              <td className="trade-log-empty" colSpan={columns.length + 1}>
                No trade entry/exit logs found.
              </td>
            </tr>
          )}
        </tbody>
        </table>
      </div>

      <div className="trade-log-pagination">
        <div className="trade-log-page-size">
          <label htmlFor="trade-log-page-size">Records per page</label>
          <select
            id="trade-log-page-size"
            value={pageSize}
            onChange={(event) => {
              const value = event.target.value;
              setPageSize(value === 'all' ? 'all' : Number(value));
              setPage(1);
            }}
          >
            <option value={10}>10</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value="all">All</option>
          </select>
        </div>

        <div className="trade-log-page-navigation" aria-label="Trade entry/exit log pagination">
          <button
            type="button"
            disabled={page === 1 || pageSize === 'all'}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
          >
            Previous
          </button>
          <span>Page {page} of {totalPages}</span>
          <button
            type="button"
            disabled={page === totalPages || pageSize === 'all'}
            onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
          >
            Next
          </button>
        </div>

        <div className="trade-log-record-total">
          Showing {firstVisibleRecord}-{lastVisibleRecord} of {sortedLogs.length} records
          {filteredLogs.length !== flatLogs.length && ` (filtered from ${flatLogs.length} total)`}
        </div>
      </div>
    </div>
  );
}
